import React, { createContext, ReactNode, useContext, useEffect, useRef, useState } from 'react'
import { fetchWishlist } from '@features/account/services/wishlist'
import { useAuthentication } from '@auth/hooks/useAuthentication'
import { MkAuthenticationService } from '@auth/services/AuthService/_authService'
import { useAuthHeaders } from '@hooks/useAuthHeaders'
import { normalizeProductHubId } from '@utils/resortUtils'
import type { TMkSession } from '@features/authentication/types'

export interface IWishlistResort {
  resortId: string
  primaryImage?: string
  prodName: string
  resourceKey: string
  city: string
  stateFull: string
  state: string
  vacationExperiences?: string
  averageRating?: number
}

interface WishlistContextType {
  wishlistItems: IWishlistResort[]
  isLoading: boolean
  wishlistError: boolean
  isInWishlist: (productId: string) => boolean
  refreshWishlist: () => Promise<void>
  addToWishlist: (productId: string) => Promise<boolean>
  removeFromWishlist: (productId: string) => Promise<boolean>
}

const WishlistContext = createContext<WishlistContextType | undefined>(undefined)

export const useWishlist = () => {
  const context = useContext(WishlistContext)
  if (!context) {
    throw new Error('useWishlist must be used within a WishlistProvider')
  }
  return context
}

interface WishlistProviderProps {
  children: ReactNode
}

export const WishlistProvider: React.FC<WishlistProviderProps> = ({ children }) => {
  const [wishlistItems, setWishlistItems] = useState<IWishlistResort[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [wishlistError, setWishlistError] = useState(false)
  const { session } = useAuthentication()
  const authHeaders = useAuthHeaders()
  const hasFetchedRef = useRef(false)
  const jwtTokenRef = useRef<string | undefined>()
  const lastSessionRef = useRef<TMkSession | undefined>(undefined)

  const refreshWishlist = async () => {
    if (!authHeaders['X-JWT-Token'] || !authHeaders['Access-Code']) {
      setWishlistItems([])
      setWishlistError(false)
      hasFetchedRef.current = false
      return
    }

    try {
      setIsLoading(true)
      setWishlistError(false)
      const wishlistData = await fetchWishlist(authHeaders)
      setWishlistItems(wishlistData.resorts || [])
      hasFetchedRef.current = true
    } catch (error) {
      console.error('Failed to fetch wishlist:', error)
      setWishlistItems([])
      setWishlistError(true)
      hasFetchedRef.current = false
    } finally {
      setIsLoading(false)
    }
  }

  const isInWishlist = (productId: string): boolean => {
    const normalizedProductId = normalizeProductHubId(productId)
    return wishlistItems.some((resort) => resort.resortId === normalizedProductId)
  }

  const addToWishlist = async (productId: string): Promise<boolean> => {
    try {
      const normalizedProductId = normalizeProductHubId(productId)
      const { addToWishlist: addToWishlistApi } = await import('@account/services/wishlist')
      const success = await addToWishlistApi(normalizedProductId, authHeaders)
      if (success) {
        // Optimistic update - add the resort to the list
        // We'll need to get the resort details from somewhere
        // For now, we'll refresh to get the full data
        await refreshWishlist()
      }
      return success
    } catch (error) {
      console.error('Failed to add to wishlist:', error)
      return false
    }
  }

  const removeFromWishlist = async (productId: string): Promise<boolean> => {
    try {
      const normalizedProductId = normalizeProductHubId(productId)
      const { removeFromWishlist: removeFromWishlistApi } = await import('@account/services/wishlist')
      const success = await removeFromWishlistApi(normalizedProductId, authHeaders)
      if (success) {
        // Optimistic update - immediately remove from the list
        setWishlistItems((prev) => prev.filter((resort) => resort.resortId !== normalizedProductId))
      }
      return success
    } catch (error) {
      console.error('Failed to remove from wishlist:', error)
      return false
    }
  }

  // Only fetch wishlist when we have valid auth headers and haven't fetched yet
  // or when the JWT token changes (indicating a new login)
  useEffect(() => {
    const currentJwtToken = authHeaders['X-JWT-Token']

    // If we have valid auth headers and either haven't fetched yet or the JWT token changed
    if (currentJwtToken && authHeaders['Access-Code']) {
      // While TTL is expired, AuthGuard refreshes tokens first; avoid requests with stale JWT/accessCode.
      if (MkAuthenticationService.areMakoAuthTokensExpired()) {
        return
      }

      const jwtChanged = jwtTokenRef.current !== currentJwtToken
      if (!hasFetchedRef.current || jwtChanged) {
        jwtTokenRef.current = currentJwtToken
        lastSessionRef.current = session
        refreshWishlist()
        return
      }

      // Authorization refreshed with identical header strings (e.g. after idle TTL refresh) — bust wishlist cache.
      if (lastSessionRef.current !== session) {
        lastSessionRef.current = session
        refreshWishlist()
      }
    } else {
      // Clear wishlist if no valid auth headers
      setWishlistItems([])
      setWishlistError(false)
      hasFetchedRef.current = false
      jwtTokenRef.current = undefined
      lastSessionRef.current = undefined
    }
  }, [authHeaders['X-JWT-Token'], authHeaders['Access-Code'], session])

  const value = React.useMemo<WishlistContextType>(
    () => ({
      wishlistItems,
      isLoading,
      wishlistError,
      isInWishlist,
      refreshWishlist,
      addToWishlist,
      removeFromWishlist,
    }),
    [wishlistItems, isLoading, wishlistError],
  )

  return <WishlistContext.Provider value={value}>{children}</WishlistContext.Provider>
}
